<template>
  <q-page style="padding: 24px;">
    <div style="max-width: 960px; margin: 0 auto;" v-if="user">
      <!-- Section 1: Header -->
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <div>
          <h1
            :style="{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--oa-text-primary)',
              margin: 0,
              lineHeight: 1.3,
            }"
          >
            Hola, {{ user.firstName }}
          </h1>
          <p
            :style="{
              fontSize: '0.75rem',
              color: 'var(--oa-text-muted)',
              margin: '2px 0 0 0',
            }"
          >
            {{ formattedStage }} &middot; {{ roleLabel }}
          </p>
        </div>
        <div v-if="levelInfo.xpToNext > 0" style="text-align: right;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <div
              :style="{
                width: '96px',
                height: '6px',
                borderRadius: '3px',
                background: 'var(--oa-bg-3)',
                overflow: 'hidden',
              }"
            >
              <div
                :style="{
                  width: levelInfo.progress + '%',
                  height: '6px',
                  borderRadius: '3px',
                  background: 'linear-gradient(90deg, var(--oa-gold-mid), var(--oa-gold-full))',
                  transition: 'width 0.3s ease',
                }"
              />
            </div>
            <span
              :style="{
                fontSize: '10px',
                fontFamily: 'var(--oa-font-mono)',
                color: 'var(--oa-gold-mid)',
              }"
            >
              {{ levelInfo.xpToNext }} AURA
            </span>
          </div>
        </div>
      </div>

      <!-- Section 2: Stats Row -->
      <div
        :style="{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
          marginTop: '20px',
        }"
      >
        <!-- AURA stat -->
        <div
          :style="{
            background: 'var(--oa-bg-2)',
            border: '1px solid var(--oa-border)',
            borderRadius: '12px',
            padding: '14px',
          }"
        >
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
            <div
              :style="{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'color-mix(in srgb, var(--oa-gold-full) 12%, transparent)',
                color: 'var(--oa-gold-full)',
              }"
            >
              <q-icon name="bolt" size="16px" />
            </div>
            <span
              :style="{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                fontFamily: 'var(--oa-font-mono)',
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--oa-text-primary)',
              }"
            >
              {{ user.xp.toLocaleString() }}
            </span>
          </div>
          <span style="font-size: 11px; color: var(--oa-text-muted);">AURA</span>
        </div>

        <!-- Streak stat -->
        <div
          :style="{
            background: 'var(--oa-bg-2)',
            border: '1px solid var(--oa-border)',
            borderRadius: '12px',
            padding: '14px',
          }"
        >
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
            <div
              :style="{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'color-mix(in srgb, var(--color-terracotta) 12%, transparent)',
                color: 'var(--color-terracotta)',
              }"
            >
              <q-icon name="local_fire_department" size="16px" />
            </div>
            <span
              :style="{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                fontFamily: 'var(--oa-font-mono)',
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--oa-text-primary)',
              }"
            >
              {{ user.currentStreak }}
            </span>
          </div>
          <span style="font-size: 11px; color: var(--oa-text-muted);">
            {{ user.currentStreak === 1 ? 'Dia de racha' : 'Dias de racha' }}
          </span>
        </div>

        <!-- Best streak stat -->
        <div
          :style="{
            background: 'var(--oa-bg-2)',
            border: '1px solid var(--oa-border)',
            borderRadius: '12px',
            padding: '14px',
          }"
        >
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
            <div
              :style="{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'color-mix(in srgb, var(--oa-tier-cc) 12%, transparent)',
                color: 'var(--oa-tier-cc)',
              }"
            >
              <q-icon name="trending_up" size="16px" />
            </div>
            <span
              :style="{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                fontFamily: 'var(--oa-font-mono)',
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--oa-text-primary)',
              }"
            >
              {{ user.longestStreak }}
            </span>
          </div>
          <span style="font-size: 11px; color: var(--oa-text-muted);">Mejor racha</span>
        </div>
      </div>

      <!-- Section 3: Feature Cards -->
      <div style="margin-top: 20px;">
        <!-- Continue Learning card (full-width) -->
        <div
          :style="{
            background: 'var(--oa-bg-2)',
            border: '1px solid var(--oa-border)',
            borderRadius: '12px',
            padding: '20px',
            borderLeft: '4px solid var(--oa-gold-dim)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }"
        >
          <div
            :style="{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'color-mix(in srgb, var(--oa-gold-full) 12%, transparent)',
              flexShrink: 0,
            }"
          >
            <q-icon name="menu_book" size="24px" style="color: var(--oa-gold-full);" />
          </div>
          <div>
            <div
              :style="{
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--oa-text-primary)',
                marginBottom: '4px',
              }"
            >
              Tu curso te espera
            </div>
            <div style="font-size: 0.875rem; color: var(--oa-text-secondary);">
              Los cursos y lecciones estaran disponibles proximamente.
            </div>
          </div>
        </div>
      </div>

      <!-- Two-column cards -->
      <div
        :style="{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '12px',
          marginTop: '12px',
        }"
      >
        <!-- AURA & Logros card -->
        <div
          :style="{
            background: 'var(--oa-bg-2)',
            border: '1px solid var(--oa-border)',
            borderRadius: '12px',
            padding: '16px',
          }"
        >
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
            <q-icon name="emoji_events" size="20px" style="color: var(--oa-gold-full);" />
            <span style="font-size: 0.875rem; font-weight: 600; color: var(--oa-text-primary);">
              AURA y Logros
            </span>
          </div>
          <div
            :style="{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              fontFamily: 'var(--oa-font-mono)',
              color: 'var(--oa-gold-full)',
              marginBottom: '4px',
            }"
          >
            {{ user.xp.toLocaleString() }} AURA
          </div>
          <p style="font-size: 0.75rem; color: var(--oa-text-muted); margin: 0;">
            Completa lecciones y quizzes para ganar AURA.
          </p>
        </div>

        <!-- Tu Progreso card -->
        <div
          :style="{
            background: 'var(--oa-bg-2)',
            border: '1px solid var(--oa-border)',
            borderRadius: '12px',
            padding: '16px',
          }"
        >
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
            <q-icon name="trending_up" size="20px" style="color: var(--oa-tier-cc);" />
            <span style="font-size: 0.875rem; font-weight: 600; color: var(--oa-text-primary);">
              Tu Progreso
            </span>
          </div>
          <div
            :style="{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--oa-text-primary)',
              marginBottom: '4px',
            }"
          >
            {{ user.currentStreak }} dias de racha
          </div>
          <p style="font-size: 0.75rem; color: var(--oa-text-muted); margin: 0;">
            Mantene tu racha completando lecciones cada dia.
          </p>
        </div>
      </div>

      <!-- Role-based cards -->
      <!-- Admin card -->
      <div
        v-if="user.role === 'admin' || user.role === 'super_admin'"
        :style="{
          background: 'var(--oa-bg-2)',
          border: '1px solid var(--oa-border)',
          borderRadius: '12px',
          padding: '16px',
          marginTop: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }"
      >
        <div style="display: flex; align-items: center; gap: 12px;">
          <q-icon name="admin_panel_settings" size="24px" style="color: var(--oa-gold-full);" />
          <div>
            <div style="font-size: 0.875rem; font-weight: 600; color: var(--oa-text-primary);">
              Panel de Administracion
            </div>
            <div style="font-size: 0.75rem; color: var(--oa-text-muted);">
              Gestiona usuarios, cursos y certificados.
            </div>
          </div>
        </div>
        <q-btn
          outline
          no-caps
          label="Ir al panel"
          to="/admin"
          :style="{ color: 'var(--oa-gold-full)', borderColor: 'var(--oa-gold-dim)' }"
        />
      </div>

      <!-- OT Dashboard card -->
      <div
        v-if="user.role === 'ot' || user.role === 'ot_candidato'"
        :style="{
          background: 'var(--oa-bg-2)',
          border: '1px solid var(--oa-border)',
          borderRadius: '12px',
          padding: '16px',
          marginTop: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }"
      >
        <div style="display: flex; align-items: center; gap: 12px;">
          <q-icon name="analytics" size="24px" style="color: var(--oa-gold-full);" />
          <div>
            <div style="font-size: 0.875rem; font-weight: 600; color: var(--oa-text-primary);">
              Dashboard OT
            </div>
            <div style="font-size: 0.75rem; color: var(--oa-text-muted);">
              AAP, KPIs y metricas de tu sede.
            </div>
          </div>
        </div>
        <q-btn
          outline
          no-caps
          label="Ver dashboard"
          to="/ot"
          :style="{ color: 'var(--oa-gold-full)', borderColor: 'var(--oa-gold-dim)' }"
        />
      </div>
    </div>

    <!-- Loading state -->
    <div
      v-else
      style="max-width: 960px; margin: 0 auto; padding: 24px;"
    >
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px;">
        <div>
          <q-skeleton type="text" width="180px" height="24px" dark />
          <q-skeleton type="text" width="120px" height="14px" dark style="margin-top: 4px;" />
        </div>
        <q-skeleton type="rect" width="120px" height="6px" dark />
      </div>
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;">
        <q-skeleton type="rect" height="80px" dark style="border-radius: 12px;" />
        <q-skeleton type="rect" height="80px" dark style="border-radius: 12px;" />
        <q-skeleton type="rect" height="80px" dark style="border-radius: 12px;" />
      </div>
      <q-skeleton type="rect" height="80px" dark style="border-radius: 12px; margin-top: 20px;" />
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-top: 12px;">
        <q-skeleton type="rect" height="120px" dark style="border-radius: 12px;" />
        <q-skeleton type="rect" height="120px" dark style="border-radius: 12px;" />
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useAuthStore } from 'stores/useAuthStore';

const authStore = useAuthStore();
const user = computed(() => authStore.user);

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  aspirante: 'Aspirante',
  cc: 'Coach Certified',
  sc: 'Senior Coach',
  c1: 'Coach Nivel 1',
  c2: 'Coach Nivel 2',
  ot_candidato: 'OT Candidato',
  ot: 'Owner Trainer',
  kairos: 'Kairos',
  gj: 'GJ',
  lider_nacional: 'Lider Nacional',
  lider_regional: 'Lider Regional',
  evaluador: 'Evaluador',
  mentor: 'Mentor',
  admin: 'Administrador',
  super_admin: 'Super Admin',
};

const LEVEL_THRESHOLDS = [0, 100, 300, 600, 1000, 1500, 2200, 3000, 4000, 5500, 7500, 10000];

function getLevel(xp: number) {
  let level = 0;
  for (let i = 0; i < LEVEL_THRESHOLDS.length; i++) {
    if (xp >= LEVEL_THRESHOLDS[i]) level = i;
  }
  const current = LEVEL_THRESHOLDS[level] ?? 0;
  const next = LEVEL_THRESHOLDS[level + 1] ?? current;
  const progress = next > current ? ((xp - current) / (next - current)) * 100 : 100;
  const xpToNext = next > current ? next - xp : 0;
  return { level, progress, xpToNext };
}

const levelInfo = computed(() => {
  if (!user.value) return { level: 0, progress: 0, xpToNext: 0 };
  return getLevel(user.value.xp);
});

const formattedStage = computed(() => {
  if (!user.value) return '';
  return user.value.careerStage
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
});

const roleLabel = computed(() => {
  if (!user.value) return '';
  return ROLE_LABELS[user.value.role] ?? user.value.role;
});
</script>
